import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ContactList } from '@/components/ContactList';
import { useAuth, signOut } from '@/lib/auth/useAuth';
import { navigate } from '@/lib/router/useHashRoute';
import { getSupabase } from '@/lib/supabase';
import { getLocalizedRoleLabel } from '@/lib/i18n/roleLabel';
import { isLang, type Lang } from '@/types/lang';

/**
 * Sidebar — Family (CLAUDE.md §7 layout responsiveness, A6 — iPad two-pane)
 *
 * Fixed 320px-wide left pane shown only on tablet (≥820px composite). Stays
 * mounted across navigations inside the two-pane layout so the contact list,
 * face-to-face entries, and bottom utilities stay one tap away while the
 * right pane handles routing.
 *
 * Structure:
 *   - App logo + title at top
 *   - "Ailen" heading + <ContactList compact /> driving the right pane via
 *     `?c=<convId>` query (no path change → Home stays mounted)
 *   - "Yüzyüze konuşma" heading + list of contacts that route to F2F (which
 *     escapes the two-pane wrapper and renders fullscreen — see App.tsx)
 *   - Settings + sign-out at the bottom
 *
 * The component never renders on phone — App.tsx gates it with `useViewport`.
 * Even so, all styles use logical CSS (`me-*`/`ms-*` not needed here, but
 * `border-r` is OK since BS/DE/TR/EN are all LTR).
 */

interface SidebarProps {
  /** Conversation id currently open in the right pane (for active highlight). */
  activeConversationId: string | null;
  /** Called when a contact tap resolves to a conversation id. */
  onOpenConversation: (conversationId: string) => void;
}

interface ContactRow {
  id: string;
  name: string;
  role_label: string;
  role_label_translations: Record<string, string> | null;
}

export function Sidebar({ activeConversationId, onOpenConversation }: SidebarProps) {
  const { t, i18n } = useTranslation();
  const uiLang: Lang = isLang(i18n.language) ? i18n.language : 'tr';
  const { session } = useAuth();
  const myId = session?.user.id ?? null;

  // Same query key as ContactList → already-cached read, no extra network.
  const contactsQuery = useQuery({
    queryKey: ['contacts', myId],
    enabled: Boolean(myId),
    queryFn: async (): Promise<ContactRow[]> => {
      const supabase = getSupabase();
      const { data, error } = await supabase
        .from('users')
        .select('id, name, role_label, role_label_translations')
        .order('name', { ascending: true });
      if (error) throw error;
      const rows = (data ?? []).map<ContactRow>((u) => ({
        id: u.id as string,
        name: u.name as string,
        role_label: u.role_label as string,
        role_label_translations:
          u.role_label_translations && typeof u.role_label_translations === 'object'
            ? (u.role_label_translations as Record<string, string>)
            : null,
      }));
      return rows.filter((u) => u.id !== myId);
    },
    refetchInterval: 8000,
  });

  const f2fContacts = contactsQuery.data ?? [];

  return (
    <aside
      aria-label={t('home.contactsHeading')}
      className="w-[320px] flex-shrink-0 h-screen bg-bg-elevated border-r border-border-subtle flex flex-col"
    >
      <header className="px-5 py-5 border-b border-border-subtle">
        <button
          type="button"
          onClick={() => onOpenConversation('')}
          className="text-left w-full"
        >
          <h1 className="font-serif italic text-2xl text-text-primary leading-none">
            {t('app.title')}
          </h1>
          <div className="w-9 h-px bg-accent-gold mt-1 mb-2" aria-hidden="true" />
          <p className="text-sm text-text-secondary font-serif italic">{t('app.subtitle')}</p>
        </button>
      </header>

      <nav className="flex-1 overflow-y-auto px-4 py-4 flex flex-col gap-6">
        <section>
          <p className="eyebrow mb-2 px-1">{t('home.contactsHeading')}</p>
          <ContactList
            compact
            activeConversationId={activeConversationId}
            onOpenConversation={onOpenConversation}
          />
        </section>

        {f2fContacts.length > 0 ? (
          <section>
            <div className="ornament-divider mb-3" aria-hidden="true">
              <span>✦</span>
            </div>
            <p className="eyebrow mb-2 px-1 text-center">
              {t('faceToFace.headerTitle')}
            </p>
            <ul className="flex flex-col gap-1">
              {f2fContacts.map((contact) => {
                const localizedRole = getLocalizedRoleLabel(contact, uiLang);
                return (
                <li key={contact.id}>
                  <button
                    type="button"
                    onClick={() => navigate(`/face-to-face/${contact.id}`)}
                    aria-label={`${t('faceToFace.entryButton')} — ${localizedRole}`}
                    className="w-full min-h-tap flex items-center justify-between gap-2 px-3 py-2 rounded-xl border border-border-subtle hover:border-accent-gold text-left transition-colors duration-150"
                  >
                    <span className="flex flex-col min-w-0">
                      <span className="text-sm font-serif text-text-primary truncate">
                        {localizedRole}
                      </span>
                    </span>
                    <span
                      aria-hidden="true"
                      className="text-sm text-accent-gold-dark flex-shrink-0 font-serif italic"
                    >
                      →
                    </span>
                  </button>
                </li>
                );
              })}
            </ul>
          </section>
        ) : null}
      </nav>

      <footer className="border-t border-border-subtle px-4 py-3 flex flex-col gap-2">
        <button
          type="button"
          onClick={() => navigate('/family-code')}
          className="w-full min-h-tap px-3 py-2 rounded-xl text-sm text-accent-gold-dark border border-accent-gold hover:bg-bg-bubble-received/40 text-left font-serif italic"
        >
          ✦ &nbsp;{t('home.showFamilyCodeButton')}
        </button>
        <button
          type="button"
          onClick={() => navigate('/settings')}
          className="w-full min-h-tap px-3 py-2 rounded-xl text-sm text-text-primary border border-border-subtle hover:border-accent-gold text-left font-serif italic"
        >
          {t('home.settingsButton')}
        </button>
        <button
          type="button"
          onClick={() => void signOut()}
          className="w-full min-h-tap px-3 py-2 rounded-xl text-sm text-accent-warning text-left font-serif italic gold-underline"
        >
          {t('settings.signOut')}
        </button>
      </footer>
    </aside>
  );
}
